import type { ComponentPropsWithRef } from "react";
import { forwardRef } from "react";
import { omit } from "lodash";
import { useGetForm } from "@/react/components/SettingsApp/Emails/components/Forms/hooks/useGetForm";
import type { StyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import { parseSizing } from "@circle-react-shared/BuilderUI/utils/parseSizing";
import { parseStyles } from "@circle-react-shared/BuilderUI/utils/parseStyles";
import { classNames } from "@circle-react-shared/BuilderUI/utils/twMergeWithCNAndPrefix";
import { FormEmbed } from "./FormEmbed";
import { FormError } from "./FormError";
import { NoFormSelected } from "./NoFormSelected";
import { defaultStyles } from "./defaultStyles";

interface FormBlockProps extends ComponentPropsWithRef<"div"> {
  background_color?: string;
  "data-preview-mode": boolean;
  form_id?: number | null;
  styles?: Record<string, StyleValue>;
}

export const FormBlock = forwardRef<HTMLDivElement, FormBlockProps>(
  (
    {
      "data-preview-mode": isPreviewMode,
      form_id,
      background_color,
      styles = {},
      className,
      ...rest
    },
    ref,
  ) => {
    const {
      data: form,
      isLoading: isFormLoading,
      error: formError,
    } = useGetForm({
      id: form_id || undefined,
    });

    const inlineStyles = {
      ...parseStyles({
        styles: omit(styles, ["sizing", "backgroundColor"]),
        defaultStyles,
      }),
      ...parseSizing({
        sizing: styles.sizing,
        defaultSizing: defaultStyles.sizing,
      }),
      ...(background_color && { backgroundColor: background_color }),
    };

    const containerClasses = classNames(
      "wb-min-w-80 wb-min-h-32 wb-w-full wb-max-w-full wb-justify-self-center",
      {
        "wb-flex wb-items-center wb-justify-center wb-border-2 wb-border-dashed wb-border-primary wb-rounded-lg wb-bg-secondary":
          !form_id,
      },
      className,
    );

    return (
      <div
        ref={ref}
        className={containerClasses}
        style={inlineStyles}
        {...rest}
      >
        {!form_id ? (
          <NoFormSelected />
        ) : isFormLoading ? null : formError || !form ? (
          <FormError formError={formError} />
        ) : (
          <FormEmbed isPreviewMode={isPreviewMode} form={form} />
        )}
      </div>
    );
  },
);

FormBlock.displayName = "FormBlock";
